import { useEffect, useState, useReducer } from "react";
import { IDynamicFormCaseProps } from "./IDynamicFieldRenderer";
import { FormInput } from "../FormInputs/FormInputs";
import { FormSelect } from "../FormSelect";
import { FormCascade } from "../FormCascade";
import { FormDate } from "../FormDate";
import FormFileUpload from "../FormFileUpload";
import { Button, Col, Form, Row } from "antd";
import FormTable from "../DynamicTable/FormTable";
import { FormRadioButtons } from "../FormRadioButtons";
import { FormCheckbox } from "../FormCheckbox";
import "./DynamicFieldRenderer.css";
import FormFieldSetComponent from "../FormFieldSet/FormFieldSet";
import FormInlineGrid from "../DynamicTable/FormInlineTable";
import { API } from "../../Axios/Axios";
import { getCascadeFieldOptions, getEmployeeDropdownUserAccess } from "../../Helpers/APIEndpoints/Endpoints";
import dayjs from "dayjs";
import DocumentView from "../../Pages/DocumentView/DocumentView";
import { BASE_URL } from "../../Helpers/APICalls/Constants";
const DynamicFormCase = ({
  field,
  fields = null,
  form,
  keyIdentity,
  handleButtonClick = () => {},
  checkButtonIsLoading = () => false,
  isReset = false,
  setIsReset = () => {},
  actionType = "",
  handleFieldSetButtonClick = () => {},
  dataSource,
  colsize,
  section = {},
}: IDynamicFormCaseProps) => {
  let colsizes = colsize ? colsize : 6;
  
  const handleFieldChange = (name: string, value: any, key?: any) => {
    // Ensure correct field name formatting
    const fieldName = key ? `${name}-${key}` : name;

    // Update form state
    form?.setFieldsValue({ [fieldName]: value });

    // Store selected values
    setSelectedValues((prev) => ({ ...prev, [name]: value }));

    const currentField = fields?.find((f: { name: string }) => f.name === name);
    if (!currentField) return;

    // **Reset fields where the current field's ID is present in parentid**
    const dependentFields = fields
      ?.filter((f: { DependentFieldId: string }) =>
        f.DependentFieldId?.split(",").map(Number).includes(currentField.Id)
      )
      .map((f: { name: any }) => f.name);

    dependentFields?.forEach((field: any) => {
      form?.setFieldsValue({ [field]: [] });
    });
  };

  const parseDateValidationRules = (
    rulesFromApi: any[],
    getFieldValue: any,
    label: any
  ) => {
    return rulesFromApi?.flatMap((rule) => {
      const rules: any[] = [];

      if (rule.required) {
        rules.push({
          required: true,
          message: `${label} is required`,
        });
      }

      if (rule.minDate || rule.maxDate) {
        rules.push({
          validator: (_: any, value: any) => {
            if (!value) return Promise.resolve(); // Let 'required' rule handle this

            const resolveDate = (rawDate: string | null) => {
              if (!rawDate) return null;

              const dynamicPattern = /^\[([A-Za-z]+)([+-]?\d*)\]$/;

              const match = rawDate.match(dynamicPattern);
              if (match) {
                const keyword = match[1];
                const offset = parseInt(match[2] || "0", 10);

                if (keyword === "Today") {
                  return dayjs().add(offset, "day");
                } else {
                  const fieldValue = getFieldValue(keyword);
                  if (!fieldValue) return null;
                  return dayjs(fieldValue).add(offset, "day");
                }
              }

              return dayjs(rawDate); // Assume static date
            };

            const minDate = resolveDate(rule.minDate);
            const maxDate = resolveDate(rule.maxDate);

            if (minDate && dayjs(value).isBefore(minDate, "day")) {
              return Promise.reject(
                `Date must be after ${minDate.format("YYYY-MM-DD")}`
              );
            }

            if (maxDate && dayjs(value).isAfter(maxDate, "day")) {
              return Promise.reject(
                `Date must be before ${maxDate.format("YYYY-MM-DD")}`
              );
            }

            return Promise.resolve();
          },
        });
      }

      return rules;
    });
  };

  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const [isChecked, setIsChecked] = useState(field.defaultValue ?? false);

  const handleClick = async (name: string) => {
    const currentField = fields?.find((f: { name: string }) => f?.name === name);
    if (!currentField) return;

    if (currentField.lazyload) {
      const data = form.getFieldsValue();

      // Validate required execution parameters
      let hasError = false;
      const execParamsList = (currentField.execParams || []).reduce(
        (acc: { [x: string]: any }, param: { name: string | number }) => {
          const paramValue = data[param.name] ?? "";

          if (paramValue === "") {
            hasError = true;
            acc[param.name] = `${param.name} is required`; // Store error message
          } else {
            acc[param.name] = paramValue;
          }
          return acc;
        },
        {}
      );

      // Stop execution if validation fails and show specific error messages in dropdown
      if (hasError) {
        currentField.fieldOptions = Object.values(execParamsList)
          .filter((msg) => (msg as string).includes("is required")) // Show only required errors
          .map((msg) => ({ label: msg as string, value: "" }));

        forceUpdate();
        return;
      }

      try {
        const response = await fetchDropdownData(
          currentField.Id,
          execParamsList
        );

        if (Array.isArray(response.data)) {
          currentField.fieldOptions = response.data.length
            ? response.data
            : [{ label: "No Data", value: "" }];

          if (response.data.length === 0) {
            form?.setFieldsValue({ [name]: [] });
          }
          forceUpdate();
        }
      } catch (error) {
        console.error(`Failed to load data for ${name}:`, error);
      }
    }

    // if (currentField.autoComplete) {
    //   const data = form.getFieldsValue();

    //   // Validate required execution parameters
    //   let hasError = false;
    //   const execParamsList = (currentField.execParams || []).reduce(
    //     (acc: { [x: string]: any }, param: { name: string | number }) => {
    //       const paramValue = data[param.name] ?? "";

    //       if (paramValue === "") {
    //         hasError = true;
    //         acc[param.name] = `${param.name} is required`; // Store error message
    //       } else {
    //         acc[param.name] = paramValue;
    //       }
    //       return acc;
    //     },
    //     {}
    //   );

    //   // Stop execution if validation fails and show specific error messages in dropdown
    //   if (hasError) {
    //     currentField.fieldOptions = Object.values(execParamsList)
    //       .filter((msg) => (msg as string).includes("is required")) // Show only required errors
    //       .map((msg) => ({ label: msg as string, value: "" }));

    //     forceUpdate();
    //     return;
    //   }
    //   try {
    //     const response = await fetchAutoDropdownData(
    //       currentField.Id,
    //       execParamsList,
    //       currentField.value
    //     );

    //     if (Array.isArray(response.data)) {
    //       currentField.fieldOptions = response.data.length
    //         ? response.data
    //         : [{ label: "No Data", value: "" }];

    //       if (response.data.length === 0) {
    //         form.setFieldsValue({ [name]: [] });
    //       }
    //       forceUpdate();
    //     }
    //   } catch (error) {
    //     console.error(`Failed to load data for ${name}:`, error);
    //   }
    // }

  };

  const [selectedValues, setSelectedValues] = useState<Record<string, string>>(
    {}
  );
  const [dropdownOptions, setDropdownOptions] = useState<
    Record<string, { value: string; label: string }[]>
  >({});
  const [formValues, setFormValues] = useState<{ [key: string]: any }>({
    myCheckbox: true, // Set initial value as checked
  });

  // Function to fetch dependent dropdown data
  const fetchDropdownData = async (fieldId: string, execParams: any[]) => {
    try {

      // Transform execParams in-place
      Object.keys(execParams).forEach((key: any) => {
        if (Array.isArray(execParams[key])) {
          execParams[key] = execParams[key].join(",");
        }
      });

      const response = await API.post(getCascadeFieldOptions, {
        // method: "POST",
        // headers: { "Content-Type": "application/json" },
        FieldId: fieldId,
        FormData: JSON.stringify(execParams),
        // body: JSON.stringify({
        // }),
      });

      if (response && !response.status) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return await response.data; // Parse and return the JSON response
    } catch (error) {
      console.error(`Error fetching data for ${fieldId}:`, error);
      return []; // Return an empty array in case of an error
    }
  };

  const fetchAutoDropdownData = async (fieldId: string, execParams: any, fieldApi: string) => {
    console.log('inside auto dropdown and field api =>', fieldApi, ' fieldId=>', fieldId, ' execParams=>', execParams)
    if (fieldId == "7") {
      fieldApi = BASE_URL + "/api/Common/GetAzureADUser";
      //fieldApi = getEmployeeDropdownUserAccess;
    }
    console.log('execparams in auto ========>', execParams)
    try {
      const response = await fetch(fieldApi, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          FieldId: fieldId,
          FormData: JSON.stringify(execParams),
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return await response.json(); // Parse and return the JSON response
    } catch (error) {
      console.error(`Error fetching data for ${fieldId}:`, error);
      return []; // Return an empty array in case of an error
    }
  };

  const handleSearch = async (e: any, name: string) => {
    console.log('form===>', form)
    console.log('event in handle search=>', name)
    const currentField = fields.find((f: { name: string }) => f.name === name);
    console.log(currentField.Id, 'currfieldID')
    if (!currentField) return;
    const data = form.getFieldsValue();
    let hasError = false;
    console.log('data====>', data)

    // const execParamsList = (currentField.execParams || []).reduce(
    //   (acc: { [x: string]: any }, param: { name: string | number }) => {
    //     const paramValue = data[param.name] ?? "";

    //     if (paramValue === "") {
    //       hasError = true;
    //       acc[param.name] = `${param.name} is required`; // Store error message
    //     } else {
    //       acc[param.name] = paramValue;
    //     }
    //     return acc;
    //   },
    //   {}
    // );
    let execParamsList = {};
    if (e.length >= 4) {
      execParamsList = { SearchString: e };

      try {
        const response = await fetchAutoDropdownData(
          currentField.Id,
          execParamsList,
          currentField.value
        );

        console.log('response auto dropdown==>', response)

        if (Array.isArray(response.data)) {
          currentField.fieldOptions = response.data.length
            ? //response.data.map((e:any)=> { return{label:e.Name,value : e.id}})
            response.data.map((item: any) => ({
              label: item?.Name,
              value: [
                item.Name ? item.Name : '',
                item.EmailId ? item.EmailId : '',
                item.LoginId ? item.LoginId : '',
                item.firstName ? item.firstName : '',
                item.lastName ? item.lastName : ''
              ].filter(Boolean).join(' | ')
              // loginId : item?.LoginId,
              // firstName : item?.firstName,
              // lastName : item?.lastName,
            }))
            : [{ label: "No Data", value: "" }];

          // if (response.data.length === 0) {
          //   form.setFieldsValue({ [name]: [] });
          // }
          forceUpdate();
        }
      } catch (error) {
        console.error(`Failed to load data for ${name}:`, error);
      }

    }

    console.log('execParam = ===>', execParamsList)

  }


  useEffect(() => {

    if (field?.defaultValue) {
      // Dynamically setting default values after the component mounts
      handleFieldChange(field.name, field.defaultValue);
    }
  }, [form]);

  const customDateRules =
    field.inputType == "Date"
      ? parseDateValidationRules(field.rules, form.getFieldValue, field.label)
      : field.rules;

  const renderField = () => {
    if (field?.isFieldVisible) {
      
      switch (field && field.type.toLowerCase()) {
        case "fieldset":
          return (
            <FormFieldSetComponent
              // fields which are the child of the current fieldset will be send to the form field set component
              fields={fields.filter(
                (x: { parentId: any }) => x.parentId === field.fieldInterfaceId
              )}
              form={form}
              handleFieldSetButtonClick={handleFieldSetButtonClick}
              dataSource={dataSource}
              fieldSetKey={field.name}
            />
          );
  
        case "input":
          return (
            <>
              <Col lg={colsizes} md={6} sm={12} xs={24}>
                <FormInput
                  name={`${field.name}`}
                  label={field.label}
                  rules={field.rules}
                  placeholder={field.placeholder}
                  widthSize={field.widthSize}
                  textArea={field.textArea}
                  disabled={field?.isFieldDisable}
                  inputType={field.inputType || "text"}
                  onChange={(e: any) =>
                    handleFieldChange(field.name, field.inputType == "Text" ? e.target.value : e)
                  }
                  value={field.value}
                />
              </Col>
            </>
          );
  
        case "textarea":
          return (
            <>
              <Col lg={colsizes} md={6} sm={12} xs={24}>
                <FormInput
                  name={`${field.name}`}
                  label={field.label}
                  rules={field.rules}
                  placeholder={field.placeholder}
                  widthSize={field.widthSize}
                  textArea={true}
                  inputType={field.inputType || "text"}
                  rows={field.rows}
                  disabled={field?.isFieldDisable}
                  maxLength={field.maxLength}
                  onChange={(e: any) =>
                    handleFieldChange(field.name, e.target.value)
                  }
                  value={field.value}
                />
              </Col>
            </>
          );
  
        case "select":
        case "selectmulti":
          return (
            <Col lg={colsizes} md={6} sm={12} xs={24}>
              <FormSelect
                name={`${field.name}`}
                label={field.label}
                rules={field.rules}
                disabled={field?.isFieldDisable}
                placeholder={field.placeholder}
                widthSize={field.widthSize}
                options={
                  dropdownOptions[field.name] ??
                  field.fieldOptions?.map(
                    (opt: { value: any; label: any }) => ({
                      value: String(opt.value),
                      label: opt.label,
                    })
                  )
                }
                mode={
                  field.type.toLowerCase() === "selectmulti"
                    ? "multiple"
                    : undefined
                }
                onChange={(value: any) => {
                  console.log("onchange value=>", value);
                  handleFieldChange(field.name, value, keyIdentity);
                }}
                filterOption={true}
                onSearch={(e: any) => handleSearch(e, field.name)}
                onClick={() => handleClick(field.name)} // Trigger onClick
              />
            </Col>
          );
  
        case "cascade":
          return (
            <Col lg={colsizes} md={6} sm={12} xs={24}>
              <FormCascade
                name={`${field.name}`}
                label={field.label}
                rules={field.rules}
                disabled={field?.isFieldDisable}
                placeholder={field.placeholder}
                widthSize={field.widthSize}
                options={field.fieldOptions}
                onChange={(value: any) => handleFieldChange(field.name, value)}
              />
            </Col>
          );
  
        case "date":
          return (
            <Col lg={colsizes} md={6} sm={12} xs={24}>
              <FormDate
                name={`${field.name}`}
                label={field.label}
                widthSize={field.widthSize}
                rules={customDateRules}
                format={field.format}
                onChange={(date: any) => handleFieldChange(field.name, date)}
                disabled={field?.isFieldDisable}
                defaultValue={field?.defaultValue}
                minDate={field?.minDate}
                maxDate={field?.maxDate}
                placeholder={field.placeholder}
              />
            </Col>
          );
  
        case "file":
        case "dragfile":
          return (
            <Col lg={colsizes}>
              <FormFileUpload
                type={field.type}
                name={`${field.name}`}
                form={form}
                acceptedFileTypes={[field.acceptedFileTypes]}
                max={field.maxFileCount}
                widthSize={field.widthSize}
                disabled={field?.isFieldDisable}
                label={field.label}
                rules={field.rules}
              />
            </Col>
          );
  
        case "checkbox":
          return (
            <Col lg={colsizes}>
              <FormCheckbox
                name={`${field.name}`}
                label={field.label}
                required={field.rules?.some((rule: any) => rule.required)}
                disabled={field?.isFieldDisable}
                checked={
                  actionType?.toLowerCase() === "create"
                    ? field.defaultValue
                    : Form.useWatch(field.name, form)
                }
                onChange={(e: any) =>
                  handleFieldChange(field.name, e.target.checked)
                }
              />
            </Col>
          );
  
        case "radio":
          return (
            <Col lg={colsizes}>
              <FormRadioButtons
                name={`${field.name}`}
                label={field.label}
                options={field.fieldOptions}
                required={field.rules?.some((rule: any) => rule.required)}
                onChange={(e: any) =>
                  handleFieldChange(field.name, e.target.value)
                }
              />
            </Col>
          );
  
        case "addrowtable":
          return (
            <FormTable
              columns={field.columns}
              isAddRowTable
              fields={[]}
              dataSource={field.dataSource}
              name={`${field.name}`}
              keyIdentity={keyIdentity}
              form={form}
              isReset={isReset}
              setIsReset={setIsReset}
              field={field}
              section={section}
            />
          );
  
        case "grid":
          return (
            <FormInlineGrid
              fields={fields.filter(
                (x: { parentId: any }) => x.parentId === field.fieldInterfaceId
              )}
              name={`${field.name}`}
              keyIdentity={keyIdentity}
              form={form}
              field={field}
              section={section}
            />
          );
  
        case "displayfile":
          return <DocumentView base64url={fields.base64url} />;
  
        default:
          return null;
      }
    }
  };

  return (
    <>
      {renderField()}
      {field.type.toLowerCase() === "button" && (
        <>
          {
            <>
              <Button
                className={field.label.toLowerCase() == "cancel" ? "btn-cancel" : "btn-submit"}
                loading={checkButtonIsLoading(field.actionKey.toLowerCase())}
                disabled={checkButtonIsLoading(field.actionKey.toLowerCase())}
                onClick={() => handleButtonClick(form, field)}
              >
                {checkButtonIsLoading(field.actionKey.toLowerCase())
                  ? "Processing"
                  : field.label}
              </Button>
              {isReset && (
                <Button
                  className="btn-reset"
                  onClick={() => handleButtonClick(form, "reset")}
                >
                  Reset
                </Button>
              )}
            </>
          }
        </>
      )}
    </>
  );
};

export default DynamicFormCase;
